/**
 * Feature flag utilities for WillPower Store
 * Provides centralized control over application features
 */

/**
 * Checkout mode options for phased rollout
 */
export type CheckoutMode = "guest" | "authenticated";

/**
 * Shipping mode options for phased rollout
 */
export type ShippingMode = "pickup" | "full";

/**
 * Available feature flags
 */
export type FeatureFlags = {
  /** Enable/disable commerce functionality (authentication, cart, checkout) */
  enableCommerce: boolean;
  /** Control checkout flow mode - guest (Phase 1) or authenticated (Phase 2) */
  checkoutMode: CheckoutMode;
  /** Control shipping options - pickup (Phase 1) or full (Phase 2) */
  shippingMode: ShippingMode;
};

/**
 * Get feature flag values from environment variables
 * Provides type-safe access to feature flags with proper defaults
 */
export function getFeatureFlags(): FeatureFlags {
  return {
    enableCommerce: process.env.NEXT_PUBLIC_ENABLE_COMMERCE === "true",
    checkoutMode:
      (process.env.NEXT_PUBLIC_CHECKOUT_MODE as CheckoutMode) ||
      "authenticated",
    shippingMode:
      (process.env.NEXT_PUBLIC_SHIPPING_MODE as ShippingMode) || "pickup",
  };
}

/**
 * Check if commerce functionality is enabled
 * This includes authentication, cart, checkout, and purchasing features
 */
export function isCommerceEnabled(): boolean {
  return getFeatureFlags().enableCommerce;
}

/**
 * Check if authentication features should be shown
 * Returns false when commerce is disabled (display-only mode)
 */
export function isAuthEnabled(): boolean {
  return isCommerceEnabled();
}

/**
 * Check if cart/basket functionality should be shown
 * Returns false when commerce is disabled (display-only mode)
 */
export function isCartEnabled(): boolean {
  return isCommerceEnabled();
}

/**
 * Check if checkout functionality should be available
 * Returns false when commerce is disabled (display-only mode)
 */
export function isCheckoutEnabled(): boolean {
  return isCommerceEnabled();
}

/**
 * Check if purchasing features should be available
 * Returns false when commerce is disabled (display-only mode)
 */
export function isPurchasingEnabled(): boolean {
  return isCommerceEnabled();
}

/**
 * Check if checkout is in guest mode (Phase 1)
 * Returns true when NEXT_PUBLIC_CHECKOUT_MODE=guest
 */
export function isGuestCheckoutMode(): boolean {
  return getFeatureFlags().checkoutMode === "guest";
}

/**
 * Check if checkout is in authenticated mode (Phase 2)
 * Returns true when NEXT_PUBLIC_CHECKOUT_MODE=authenticated (default)
 */
export function isAuthenticatedCheckoutMode(): boolean {
  return getFeatureFlags().checkoutMode === "authenticated";
}

/**
 * Check if shipping is in pickup-only mode (Phase 1)
 * Returns true when NEXT_PUBLIC_SHIPPING_MODE=pickup (default)
 */
export function isPickupOnlyMode(): boolean {
  return getFeatureFlags().shippingMode === "pickup";
}

/**
 * Check if shipping is in full mode (Phase 2)
 * Returns true when NEXT_PUBLIC_SHIPPING_MODE=full
 */
export function isFullShippingMode(): boolean {
  return getFeatureFlags().shippingMode === "full";
}

/**
 * Feature flag context for React components
 */
export const FEATURE_FLAGS = getFeatureFlags();
